/**
 * Guide engine v3 - headless runtime (#1024, epic #1020).
 * Spec: docs/plans/guides/spec-v3.md (R4 advance, R7 progress, R11 errors).
 *
 * The runtime is the state machine of a running guide, with NO overlay UI (that
 * is #1025). Given a loaded + validated package (#1023) it:
 *   - enters at `firstStepId` and walks FORWARD only (no Previous / undo / agenda
 *     / resume, R4 / R7 / R13);
 *   - on step entry waits for ALL of the step's highlight elements to mount before
 *     the step is "shown" (R11); a concept step (no highlight) shows at once;
 *   - evaluates the active step's `advance` array (R4): click / manual / appears /
 *     value, OR-combined, baseline captured at show for appears / value; the FIRST
 *     condition to fire applies its own `next`, so a step BRANCHES by what the user
 *     did (R1.5);
 *   - on `_exit` ends gracefully and writes `completed` via the injected callback
 *     (R7); an explicit Exit (abandon) does NOT write completed.
 *
 * It is page-agnostic: it never navigates and has no URL field. It reads
 * react-router `location` only to re-scan for a step's rings after the user's own
 * click changes the route (R5-folded). The progress-write API + catalog + launch
 * are wired in #1026; here `onCompleted` is just a callback so this layer stays
 * decoupled and unit-testable.
 */

import {
  createContext, useCallback, useContext, useEffect, useMemo, useRef, useState,
} from 'react';
import { useLocation } from 'react-router-dom';
import { EXIT } from '../contract';
import { allPresent, isPresent, readValue } from './dom';
import {
  highlightSelectors, clickSelector, watchConditions, manualButtons,
} from './stepMachine';
import { installStepGate } from './gating';

export const GuideRuntimeContext = createContext(null);

const DEFAULT_HIGHLIGHT_TIMEOUT_MS = 8000;
const DEFAULT_POLL_INTERVAL_MS = 120;

/** Runtime status. shown = the popup is live; the rest are transitional / terminal. */
export const GUIDE_STATUS = {
  IDLE: 'idle',
  WAITING: 'waiting',
  SHOWN: 'shown',
  COMPLETED: 'completed',
  EXITED: 'exited',
  ERROR: 'error',
};

/** errorReason value when a step's highlight never mounts within the timeout (R11). */
export const HIGHLIGHT_TIMEOUT_REASON = 'highlight-timeout';

export const GuideRuntimeProvider = ({
  children,
  guide = null,
  onCompleted,
  onExit,
  autoStart = false,
  highlightTimeoutMs = DEFAULT_HIGHLIGHT_TIMEOUT_MS,
  pollIntervalMs = DEFAULT_POLL_INTERVAL_MS,
}) => {
  // location read ONLY so the highlight scan re-runs after a route change; the
  // runtime stores no URL and never drives navigation (R5-folded).
  const location = useLocation();

  const [status, setStatus] = useState(GUIDE_STATUS.IDLE);
  const [stepId, setStepId] = useState(null);
  const [errorReason, setErrorReason] = useState(null);

  const byId = useMemo(
    () => new Map(((guide && guide.steps) || []).map((s) => [s.id, s])),
    [guide],
  );

  // A step advances at most once: the first firing condition wins, later ones
  // (and a double click) are ignored. Reset on every step entry.
  const firedRef = useRef(false);
  // Callbacks via refs so the effect listeners never re-subscribe on prop change.
  const onCompletedRef = useRef(onCompleted);
  onCompletedRef.current = onCompleted;
  const onExitRef = useRef(onExit);
  onExitRef.current = onExit;
  const advanceRef = useRef(() => {});

  const step = stepId != null ? byId.get(stepId) || null : null;

  const enterStep = useCallback((id) => {
    firedRef.current = false;
    setErrorReason(null);
    setStepId(id);
    const target = byId.get(id);
    const sels = target ? highlightSelectors(target) : [];
    // Concept step (no highlight) skips the mount wait and shows at once (R11).
    setStatus(sels.length === 0 ? GUIDE_STATUS.SHOWN : GUIDE_STATUS.WAITING);
  }, [byId]);

  const advance = useCallback((condition) => {
    if (firedRef.current) return;
    firedRef.current = true;
    if (condition.next === EXIT) {
      setStatus(GUIDE_STATUS.COMPLETED);
      if (guide && onCompletedRef.current) onCompletedRef.current(guide.moduleId);
      return;
    }
    enterStep(condition.next);
  }, [enterStep, guide]);
  advanceRef.current = advance;

  // Begin (or restart) the guide at firstStepId. Always restarts from the top -
  // there is no resume (R7).
  const start = useCallback(() => {
    if (!guide) return;
    enterStep(guide.firstStepId);
  }, [guide, enterStep]);

  // Abandon mid-guide. Immediate, no confirm (R7). Does NOT write completed.
  const exit = useCallback(() => {
    setStatus(GUIDE_STATUS.EXITED);
    if (onExitRef.current) onExitRef.current();
  }, []);

  // The overlay calls this when a manual button is pressed (R4).
  const selectManual = useCallback((condition) => {
    if (!condition || condition.on !== 'manual') return;
    advanceRef.current(condition);
  }, []);

  // Optional auto-start (launch surfaces own this in #1026; default off).
  const autoStartedRef = useRef(false);
  useEffect(() => {
    if (!autoStart || autoStartedRef.current) return;
    if (!guide || status !== GUIDE_STATUS.IDLE) return;
    autoStartedRef.current = true;
    start();
  }, [autoStart, guide, status, start]);

  // Highlight-wait (R11): poll until all of the step's rings mount, then show.
  // On timeout, surface the error state; the overlay offers Exit only (no retry,
  // no skip). location.pathname in deps re-scans after the user navigates.
  useEffect(() => {
    if (status !== GUIDE_STATUS.WAITING) return undefined;
    const cur = byId.get(stepId);
    if (!cur) return undefined;
    const sels = highlightSelectors(cur);
    const deadline = Date.now() + highlightTimeoutMs;
    const check = () => {
      if (allPresent(sels)) {
        setStatus(GUIDE_STATUS.SHOWN);
      } else if (Date.now() >= deadline) {
        setErrorReason(HIGHLIGHT_TIMEOUT_REASON);
        setStatus(GUIDE_STATUS.ERROR);
      }
    };
    check();
    const timer = setInterval(check, pollIntervalMs);
    return () => clearInterval(timer);
  }, [status, stepId, byId, highlightTimeoutMs, pollIntervalMs, location.pathname]);

  // Advance wiring (R4), live while shown: click listeners on ringed elements +
  // a poll for appears / value conditions against their baseline-at-show.
  useEffect(() => {
    if (status !== GUIDE_STATUS.SHOWN) return undefined;
    const cur = byId.get(stepId);
    if (!cur) return undefined;
    const cleanups = [];

    for (const cond of cur.advance) {
      if (cond.on !== 'click') continue;
      const sel = clickSelector(cur, cond.id);
      const el = sel ? document.querySelector(sel) : null;
      if (!el) continue;
      const handler = () => advanceRef.current(cond);
      el.addEventListener('click', handler);
      cleanups.push(() => el.removeEventListener('click', handler));
    }

    const watchers = watchConditions(cur);
    if (watchers.length) {
      const baseline = new Map();
      for (const c of watchers) {
        baseline.set(c, c.on === 'appears' ? isPresent(c.selector) : readValue(c.selector));
      }
      const check = () => {
        for (const c of watchers) {
          if (c.on === 'appears') {
            // fires when the result element mounts having been absent at show
            if (isPresent(c.selector) && baseline.get(c) === false) { advanceRef.current(c); return; }
          } else {
            const curVal = readValue(c.selector);
            if (c.equals != null) {
              if (curVal === c.equals) { advanceRef.current(c); return; }
            } else if (curVal !== baseline.get(c)) { advanceRef.current(c); return; }
          }
        }
      };
      const timer = setInterval(check, pollIntervalMs);
      cleanups.push(() => clearInterval(timer));
    }

    return () => { cleanups.forEach((fn) => fn()); };
  }, [status, stepId, byId, pollIntervalMs]);

  // Deny-by-default gating (#1207), live only while a gated step is shown: every
  // off-target click / pointerdown / activation key is swallowed so the user
  // cannot wander out of bounds, and off-target controls are dimmed (#1825, see
  // gating.js). Teardown on step change / exit / unmount removes the interceptor
  // and the dim, so neither outlives the step. No-op for an ungated step.
  useEffect(() => {
    if (status !== GUIDE_STATUS.SHOWN) return undefined;
    const cur = byId.get(stepId);
    if (!cur) return undefined;
    return installStepGate(cur);
  }, [status, stepId, byId]);

  const value = useMemo(() => ({
    status,
    stepId,
    step,
    errorReason,
    highlights: step ? (step.highlight || []) : [],
    manualButtons: step ? manualButtons(step) : [],
    actionHint: step ? (step.actionHint || null) : null,
    isWaiting: status === GUIDE_STATUS.WAITING,
    isShown: status === GUIDE_STATUS.SHOWN,
    isCompleted: status === GUIDE_STATUS.COMPLETED,
    isExited: status === GUIDE_STATUS.EXITED,
    isError: status === GUIDE_STATUS.ERROR,
    start,
    exit,
    selectManual,
  }), [status, stepId, step, errorReason, start, exit, selectManual]);

  return (
    <GuideRuntimeContext.Provider value={value}>{children}</GuideRuntimeContext.Provider>
  );
};

export const useGuideRuntime = () => {
  const ctx = useContext(GuideRuntimeContext);
  if (!ctx) throw new Error('useGuideRuntime must be used within GuideRuntimeProvider');
  return ctx;
};
